/*
 * CUSTOMIZED · HYPOGEN Lieferzeitanzeige · Berechnung, Version 3
 * Zieldatei: assets/hypogen-delivery-estimate.js
 * Abhängigkeiten: hypogen-delivery-calendar.js und JSON aus
 * hypogen-delivery-config.liquid; Ausgabe durch den Controller.
 *
 * Eigene Schätzung, keine native Shopify-Lieferprognose.
 * Lagerbearbeitung folgt den konfigurierten Öffnungstagen,
 * Schließungen, Annahmeschluss, Zeitzone und Bearbeitungsdauer.
 * Transport und ausgegebene Werktage zählen Montag–Freitag,
 * ohne vollständigen Transport-/Zielland-Feiertagskalender.
 * Transport beginnt nach dem berechneten Versandtag.
 *
 * Nur Berechnung: keine Netzwerk-, Speicher-, Warenkorb-, Markt-
 * oder Checkout-Schreibzugriffe. Ungültige Regeln liefern null.
 */
(() => {
  'use strict';

  function localDateKey(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date(instant));

    const values = Object.fromEntries(
      parts.map((part) => [part.type, part.value])
    );

    return `${values.year}-${values.month}-${values.day}`;
  }

  function isTransitDay(key) {
    const weekday = new Date(`${key}T00:00:00.000Z`).getUTCDay();
    return weekday >= 1 && weekday <= 5;
  }

  function addTransitDays(calendar, key, count) {
    let result = key;
    let remaining = count;

    for (let step = 0; step < 370 && remaining > 0; step += 1) {
      result = calendar.addDays(result, 1);

      if (isTransitDay(result)) {
        remaining -= 1;
      }
    }

    if (remaining > 0) {
      throw new Error('Transit range exceeds limit');
    }

    return result;
  }

  function countWeekdays(calendar, from, until) {
    let current = from;
    let count = 0;

    for (let step = 0; step < 370; step += 1) {
      if (current === until) {
        return count;
      }

      current = calendar.addDays(current, 1);

      if (isTransitDay(current)) {
        count += 1;
      }
    }

    throw new Error('Delivery range exceeds limit');
  }

  function calculate(config, countryCode, instant = new Date()) {
    const calendar = globalThis.HypogenDeliveryCalendar;

    if (!calendar || config?.estimateEnabled !== true) {
      return null;
    }

    if (
      config.transit?.dayCountingBasis !== 'monday-friday' ||
      !Array.isArray(config.transit.groups) ||
      typeof config.processing?.timeZone !== 'string' ||
      !config.processing.timeZone.trim()
    ) {
      return null;
    }

    const country = String(countryCode || '').trim().toUpperCase();

    if (!/^[A-Z]{2}$/.test(country)) {
      return null;
    }

    const matches = config.transit.groups.filter((group) => (
      group &&
      Array.isArray(group.countries) &&
      group.countries.includes(country)
    ));

    if (matches.length !== 1) {
      return null;
    }

    const { minDays, maxDays } = matches[0];

    if (
      !Number.isInteger(minDays) ||
      !Number.isInteger(maxDays) ||
      minDays < 1 || maxDays < minDays || maxDays > 60
    ) {
      return null;
    }

    try {
      const processingDate = calendar.processingStart(
        instant,
        config.processing
      );

      const today = localDateKey(
        instant,
        config.processing.timeZone
      );

      const minDate = addTransitDays(
        calendar,
        processingDate,
        minDays
      );

      const maxDate = addTransitDays(
        calendar,
        processingDate,
        maxDays
      );

      return Object.freeze({
        country,
        processingDate,
        minDate,
        maxDate,
        minWeekdays: countWeekdays(calendar, today, minDate),
        maxWeekdays: countWeekdays(calendar, today, maxDate)
      });
    } catch (_) {
      return null;
    }
  }

  globalThis.HypogenDeliveryEstimate = Object.freeze({
    calculate
  });
})();
